/**
 * PL Product Page Renderer (v2).
 *
 * Renders product detail pages using the widgetized PL format, through the v2
 * store-backed renderer (`PLPageRendererV2`): every widget is read from the unified
 * page widget store, supporting nested widgets, lazy `fetchMode`, and (later) swatch
 * refresh. Layout pagination feeds the growing widget list into the store via the
 * shared `LayoutPaginationWrapper`.
 */

import { useCallback, useEffect, useMemo } from 'react'

import { PLPageMeta, PLPageProvider } from '@src/components/pl-page-context'
import { LayoutPaginationWrapper } from '@src/components/pl-page-renderer/layout-pagination-wrapper'
import { PLPageRendererV2 } from '@src/components/pl-page-renderer/pl-page-renderer-v2'
import { useStatsigPlWidgetId } from '@src/experiments/statsig-integration/use-statsig-pl-widget-ids-config.hook'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { PL_WIDGET_BANDS } from '@src/scroll-restoration/bands'
import { useBackNavScrollRestoration } from '@src/scroll-restoration/use-back-nav-scroll-restoration'
import { PLAPIResponse } from '@src/server-side/api/fetch-pl-data'
import { ProductServerSideFeatureFlags } from '@src/server-side/server-feature-flags/feature-flag.types'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import styles from './product-page-pl-renderer.module.scss'
import { pageClass } from './product-page-pl-renderer.styles'
import { getWidgetRegistry } from './product-widget-registry'
import { ProductPageSchema } from './seo/product-page-schema'

export interface ProductPagePLRendererProps {
  plData: PLAPIResponse
  pageMeta: PLPageMeta
  serverSideFeatureFlags?: ProductServerSideFeatureFlags | null
  /** PL-provided PDP JSON-LD string, rendered verbatim. */
  jsonLd?: string
  seoSource?: 'pl-jsonld'
}

// Key within the `pl_widget_ids` Statsig dynamic config that holds the DB-unique widget id of the PDP
// reviews section (REVIEWS_FULL). Targeting is by id — never by widget type — because the BE can return
// multiple widgets of the same type; only this unique id identifies the exact reviews instance to
// load-and-scroll to. Sourcing it from Statsig lets the BE update the id without an SSR deploy.
const PL_WIDGET_ID_KEY_REVIEWS_FULL = 'reviewsFull'

// Fallback used only when the Statsig config/key is missing or Statsig is unavailable, so the
// "jump to reviews" behaviour keeps working. Per the backend team this id is currently stable.
const REVIEWS_FULL_WIDGET_ID_FALLBACK = '019e160e-5df9-7f23-958b-550be178a461'

// Container context merged into each flushed BULK_PRODUCT_CARD_VIEW event. PDP product cards
// live in recommendation/grid surfaces, so they're tagged as a product display.
const PDP_PRODUCT_CARD_TRACKING_METADATA: TrackingMetaData = {
  context: {
    contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DISPLAY,
    // eslint-disable-next-line i18next/no-literal-string -- analytics identifier, not user copy
    trackingId: 'pdp-product-display',
  },
}

export function ProductPagePLRenderer({ plData, pageMeta, jsonLd, seoSource }: Readonly<ProductPagePLRendererProps>) {
  // The registry is static per module; rebuilding it per render mints fresh `render` closures,
  // which defeats the memoized widget renderer for the whole tree on any parent-driven re-render.
  const widgetRegistry = useMemo(() => getWidgetRegistry(), [])

  // Product-card impression analytics (parity with the CLP PL renderer). The leaf
  // SingleProductCard reads `productViewTracker` off page context and pushes per-card view
  // events into the batched stream; this hook initializes + flushes that stream. Without a
  // populated tracker the PDP PL path emits zero BULK_PRODUCT_CARD_VIEW events.
  const trackerSlug = plData.meta?.slug ?? pageMeta.pathname
  const productViewTracker = `${trackerSlug}_${TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW}`

  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  // STRFRNT-15385: client mirror of the SSR enrichVersion decision (`product-page-data-pl.ts`'s
  // `options.isSvdEnabled` gates on the same Statsig experiment + kill-switch) — the canonical SVD
  // flag, same hook the V2 buy-box render path uses. Gates the lazy reco fetchers' `x-enrich-version`
  // header + `anchorAttributes` params so they stay in lock-step with the buy box this page rendered.
  const svdEnrichEnabled = useIsSvdEnabled()

  const pageMetaWithTracking: PLPageMeta = {
    ...pageMeta,
    productViewTracker,
    // PL layout/lazy-widget fetchers turn this into the `x-exp-subscription-enabled` header, matching
    // the non-PL reco flow so subscribed-price tags come back on cards. Same wiring as the CLP
    // renderer. STRFRNT-16137.
    subscriptionExpEnabled: isRecurringItemSubscriptionsExpEnabled,
    svdEnrichEnabled,
  }

  useBatchedAnalyticsForProductCard({
    productViewTracker,
    trackingMetaData: PDP_PRODUCT_CARD_TRACKING_METADATA,
  })

  // "Jump to reviews" (buy-box rating/count + "See all reviews") sets this flag. On mobile the reviews
  // are a later layout page that loads only on scroll, so `forceLoadActive` force-paginates until
  // REVIEWS_FULL arrives; the wrapper then scrolls to that widget's unique id and clears the flag here.
  const reviewSectionInFocus = useAppSelector<boolean>(pdpStateManager, pdpStateManager.getReviewSectionInFocus)
  const clearReviewFocus = useCallback(() => {
    pdpStateManager.setShowReviewSection({ reviewSectionInFocus: false })
  }, [])

  // BE-updatable (no SSR deploy) via the `pl_widget_ids` Statsig config; falls back to the known-good id.
  const reviewsFullWidgetId = useStatsigPlWidgetId(PL_WIDGET_ID_KEY_REVIEWS_FULL) ?? REVIEWS_FULL_WIDGET_ID_FALLBACK

  // Back-navigation scroll restoration (STRFRNT-15288). On Back it force-loads the widget that was at
  // the fold on leave, then re-pins that element to its saved offset. It reuses the same force-load
  // machinery as "jump to reviews", so the two share the wrapper's force-load props; back-nav restore
  // takes precedence while active and suppresses the wrapper's one-shot scroll (the hook owns placement).
  // Ungated: the PL PDP mounts on `isPLFormat`, not on the DS decision, so this template restores the
  // same way for both buckets. Only the listings/search seams read `shouldUseDesignSystem()`.
  const backNav = useBackNavScrollRestoration({ bands: PL_WIDGET_BANDS, enabled: true })

  // A back-nav scroll restore supersedes a stale "jump to reviews" intent: if the user hit "See all
  // reviews" and navigated away before `clearReviewFocus` fired, then Backs, clear the flag now so the
  // reviews jump can't fire after restore completes and yank them off the restored position
  // (STRFRNT-15288 review — dhrumilmodi1997). The flag lives in global redux and isn't reset on nav.
  useEffect(() => {
    if (backNav.active && reviewSectionInFocus) {
      clearReviewFocus()
    }
  }, [backNav.active, reviewSectionInFocus, clearReviewFocus])

  return (
    <PLPageProvider key={pageMeta.pathname} pageMeta={pageMetaWithTracking}>
      {jsonLd && <ProductPageSchema jsonLd={jsonLd} seoSource={seoSource} />}
      <div className={shouldUseDesignSystem() ? pageClass : styles.page}>
        <LayoutPaginationWrapper
          initialWidgets={plData.widgets}
          initialPagination={plData.pagination}
          widgetRegistry={widgetRegistry}
          page={pageMeta.seoData?.title ?? ''}
          renderer={PLPageRendererV2}
          forceLoadActive={backNav.active || reviewSectionInFocus}
          forceLoadTargetId={backNav.active ? backNav.targetId : reviewsFullWidgetId}
          onForceLoadComplete={backNav.active ? backNav.onComplete : clearReviewFocus}
          suppressForceLoadScroll={backNav.active}
        />
      </div>
    </PLPageProvider>
  )
}
